import { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from '@/lib/sentry-defer';
import posthog from 'posthog-js';
import { RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error);
    try {
      posthog.captureException(error, {
        $exception_source: 'react_error_boundary',
        component_stack: errorInfo.componentStack,
      });
    } catch { /* never rethrow from telemetry */ }
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="w-full px-6 py-8 flex items-center justify-center">
          <div className="w-full max-w-sm flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-[56px] h-[56px] rounded-full flex items-center justify-center bg-foreground/5 dark:bg-white/10">
                <span className="font-black text-xl tracking-tight text-foreground">J</span>
              </div>
              <span
                className="absolute bottom-0 right-0 w-[8px] h-[8px] rounded-full bg-green-500 border-2 border-background dark:border-black"
                aria-hidden
              />
            </div>
            <h2 className="font-black text-xl tracking-tight text-foreground mt-5">
              This bit broke.
            </h2>
            <p className="text-sm font-medium text-muted-foreground max-w-[260px] mt-2">
              Refresh and we'll try again.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <div className="w-full mt-3 pt-3 border-t border-border/50">
                <p className="text-xs font-mono text-muted-foreground break-all text-left">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <button
              onClick={this.handleRetry}
              className="w-full py-3.5 rounded-2xl text-sm font-black mt-6 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              style={{ background: 'hsl(var(--foreground))', color: 'hsl(var(--background))' }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
